import type { Personnel } from '../../personnel/types';
import type { ScheduleEvent } from '../../scheduler/types';
import { deriveDeadlineRecipientIdsForEvent, expandAudienceContactIds } from './context';
import type {
  CommunicationAudienceContext,
  CommunicationRecipientSnapshot,
  CommunicationRecipientSource,
  ContextualAudienceKey,
  ExcludedCommunicationRecipient,
  MissingEmailCommunicationRecipient,
  ProjectCommunicationAudience,
  ResolveCommunicationAudienceInput,
  ResolvedCommunicationRecipient,
  StandingAudienceRule,
} from './types';

interface ManualExclusion {
  audience: ProjectCommunicationAudience;
  personnelId: string;
}

interface SuppressedEmail {
  email: string;
  name?: string;
  personnelId?: string;
  reason: string;
}

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

function hasUsableEmail(personnel: Personnel): personnel is Personnel & { email: string } {
  return typeof personnel.email === 'string' && normalizeEmail(personnel.email).length > 0;
}

function audienceDisplayName(audience: ProjectCommunicationAudience): string {
  return audience.label ?? audience.name;
}

function matchesRule(personnel: Personnel, rule: StandingAudienceRule): boolean {
  if (personnel.deleted) {
    return false;
  }

  switch (rule) {
    case 'cast':
      return personnel.category === 'cast';
    case 'production-team':
      return personnel.category === 'production'
        || (personnel.category === 'crew' && personnel.isDepartmentHead === true);
    case 'stage-management':
      return personnel.department === 'stage-management';
    case 'design-team':
      return personnel.category === 'crew' && personnel.isDepartmentHead === true;
    case 'custom':
      return false;
  }
}

function mergeSourceLabels(existing: string[], incoming: string[]): string[] {
  const labels = new Set(existing);
  for (const label of incoming) {
    labels.add(label);
  }
  return [...labels];
}

function sourceKey(source: CommunicationRecipientSource): string {
  return JSON.stringify(source);
}

function mergeSources(
  existing: CommunicationRecipientSource[],
  incoming: CommunicationRecipientSource[]
): CommunicationRecipientSource[] {
  const sourcesByKey = new Map(existing.map((source) => [sourceKey(source), source]));
  for (const source of incoming) {
    sourcesByKey.set(sourceKey(source), source);
  }
  return [...sourcesByKey.values()];
}

function findRecipientIndex(
  recipients: ResolvedCommunicationRecipient[],
  personnelId: string | undefined,
  email: string
): number {
  if (personnelId) {
    const personnelIndex = recipients.findIndex((recipient) => recipient.personnelId === personnelId);
    if (personnelIndex !== -1) {
      return personnelIndex;
    }
  }

  return recipients.findIndex((recipient) => recipient.email === email);
}

function addSuppressedEmail(
  suppressedEmails: Map<string, SuppressedEmail>,
  suppressedEmail: SuppressedEmail
): void {
  if (!suppressedEmails.has(suppressedEmail.email)) {
    suppressedEmails.set(suppressedEmail.email, suppressedEmail);
  }
}

function addMissingEmailRecipient(
  missingEmailRecipients: MissingEmailCommunicationRecipient[],
  personnel: Personnel,
  sourceLabel: string,
  source: CommunicationRecipientSource
): void {
  const existing = missingEmailRecipients.find((recipient) => recipient.personnelId === personnel.id);
  if (existing) {
    existing.sourceLabels = mergeSourceLabels(existing.sourceLabels, [sourceLabel]);
    existing.sources = mergeSources(existing.sources, [source]);
    return;
  }

  missingEmailRecipients.push({
    name: personnel.name,
    personnelId: personnel.id,
    sourceLabels: [sourceLabel],
    sources: [source],
  });
}

function addExcludedRecipient(
  excludedRecipients: ExcludedCommunicationRecipient[],
  recipient: ExcludedCommunicationRecipient
): void {
  const existing = excludedRecipients.find((excluded) => (
    recipient.personnelId
      ? excluded.personnelId === recipient.personnelId
      : recipient.email !== undefined && excluded.email === recipient.email
  ) && excluded.reason === recipient.reason);

  if (existing) {
    existing.sourceLabels = mergeSourceLabels(existing.sourceLabels, recipient.sourceLabels);
    existing.sources = mergeSources(existing.sources, recipient.sources);
    return;
  }

  excludedRecipients.push(recipient);
}

function addRosterRecipient(
  recipients: ResolvedCommunicationRecipient[],
  excludedRecipients: ExcludedCommunicationRecipient[],
  missingEmailRecipients: MissingEmailCommunicationRecipient[],
  suppressedEmails: Map<string, SuppressedEmail>,
  personnel: Personnel,
  sourceLabel: string,
  source: CommunicationRecipientSource
): void {
  if (!hasUsableEmail(personnel)) {
    addMissingEmailRecipient(missingEmailRecipients, personnel, sourceLabel, source);
    return;
  }

  const email = normalizeEmail(personnel.email);
  if (personnel.emailOptOut) {
    addSuppressedEmail(suppressedEmails, {
      email,
      name: personnel.name,
      personnelId: personnel.id,
      reason: 'Email opt-out',
    });
    addExcludedRecipient(excludedRecipients, {
      email,
      name: personnel.name,
      personnelId: personnel.id,
      reason: 'Email opt-out',
      sourceLabels: [sourceLabel],
      sources: [source],
    });
    return;
  }

  const existingIndex = findRecipientIndex(recipients, personnel.id, email);
  if (existingIndex !== -1) {
    const existing = recipients[existingIndex];
    if (!existing) {
      return;
    }
    recipients[existingIndex] = {
      ...existing,
      sourceLabels: mergeSourceLabels(existing.sourceLabels, [sourceLabel]),
      sources: mergeSources(existing.sources, [source]),
    };
    return;
  }

  recipients.push({
    email,
    name: personnel.name,
    personnelId: personnel.id,
    sourceLabels: [sourceLabel],
    sources: [source],
  });
}

function addExternalRecipient(
  recipients: ResolvedCommunicationRecipient[],
  excludedRecipients: ExcludedCommunicationRecipient[],
  suppressedEmails: Map<string, SuppressedEmail>,
  externalRecipient: { email: string; name?: string },
  sourceLabel: string,
  source: CommunicationRecipientSource
): void {
  const email = normalizeEmail(externalRecipient.email);
  if (!email) {
    return;
  }
  const name = externalRecipient.name?.trim() || email;

  const suppressedEmail = suppressedEmails.get(email);
  if (suppressedEmail) {
    addExcludedRecipient(excludedRecipients, {
      email,
      name,
      reason: 'Suppressed contact email',
      sourceLabels: [sourceLabel],
      sources: [source],
    });
    return;
  }

  const existingIndex = recipients.findIndex((recipient) => recipient.email === email);
  if (existingIndex !== -1) {
    const existing = recipients[existingIndex];
    if (!existing) {
      return;
    }
    recipients[existingIndex] = {
      ...existing,
      sourceLabels: mergeSourceLabels(existing.sourceLabels, [sourceLabel]),
      sources: mergeSources(existing.sources, [source]),
    };
    return;
  }

  recipients.push({
    email,
    name,
    sourceLabels: [sourceLabel],
    sources: [source],
  });
}

function removeMissingEmailRecipient(
  missingEmailRecipients: MissingEmailCommunicationRecipient[],
  personnelId: string
): MissingEmailCommunicationRecipient[] {
  const removed: MissingEmailCommunicationRecipient[] = [];
  for (let index = missingEmailRecipients.length - 1; index >= 0; index -= 1) {
    const recipient = missingEmailRecipients[index];
    if (recipient?.personnelId === personnelId) {
      removed.unshift(recipient);
      missingEmailRecipients.splice(index, 1);
    }
  }
  return removed;
}

function removeResolvedRecipient(
  recipients: ResolvedCommunicationRecipient[],
  personnelId: string
): ResolvedCommunicationRecipient[] {
  const removed: ResolvedCommunicationRecipient[] = [];
  for (let index = recipients.length - 1; index >= 0; index -= 1) {
    const recipient = recipients[index];
    if (recipient?.personnelId === personnelId) {
      removed.unshift(recipient);
      recipients.splice(index, 1);
    }
  }
  return removed;
}

function applyManualExclusions(
  recipients: ResolvedCommunicationRecipient[],
  excludedRecipients: ExcludedCommunicationRecipient[],
  missingEmailRecipients: MissingEmailCommunicationRecipient[],
  suppressedEmails: Map<string, SuppressedEmail>,
  exclusions: ManualExclusion[],
  personnelById: Map<string, Personnel>
): void {
  for (const exclusion of exclusions) {
    const personnel = personnelById.get(exclusion.personnelId);
    if (!personnel || personnel.deleted) {
      continue;
    }

    const removedRecipients = removeResolvedRecipient(recipients, exclusion.personnelId);
    const removedMissingEmailRecipients = removeMissingEmailRecipient(
      missingEmailRecipients,
      exclusion.personnelId
    );
    const sourceLabels = [...removedRecipients, ...removedMissingEmailRecipients].reduce<string[]>(
      (labels, recipient) => mergeSourceLabels(labels, recipient.sourceLabels),
      []
    );
    const sources = [...removedRecipients, ...removedMissingEmailRecipients].reduce<CommunicationRecipientSource[]>(
      (mergedSources, recipient) => mergeSources(mergedSources, recipient.sources),
      []
    );
    const displayName = audienceDisplayName(exclusion.audience);

    if (hasUsableEmail(personnel)) {
      addSuppressedEmail(suppressedEmails, {
        email: normalizeEmail(personnel.email),
        name: personnel.name,
        personnelId: personnel.id,
        reason: `Manually excluded from ${displayName}`,
      });
    }

    addExcludedRecipient(excludedRecipients, {
      email: hasUsableEmail(personnel) ? normalizeEmail(personnel.email) : undefined,
      name: personnel.name,
      personnelId: personnel.id,
      reason: `Manually excluded from ${displayName}`,
      sourceLabels: sourceLabels.length > 0 ? sourceLabels : [displayName],
      sources: sources.length > 0
        ? sources
        : [{
          type: 'manual-include',
          audienceId: exclusion.audience.id,
          audienceName: displayName,
        }],
    });
  }
}

function resolveMeetingAttendees(
  scheduleEvents: ScheduleEvent[],
  personnel: Personnel[],
  context: ResolveCommunicationAudienceInput['context']
): string[] {
  const linkedSource = context?.linkedSource;
  if (linkedSource?.type !== 'schedule-event' || !linkedSource.id) {
    return [];
  }

  const event = scheduleEvents.find((scheduleEvent) => (
    scheduleEvent.id === linkedSource.id && scheduleEvent.type === 'meeting'
  ));

  return expandAudienceContactIds(event?.attendees ?? [], personnel);
}

function resolveCalledRecipients(
  context: CommunicationAudienceContext | undefined
): string[] {
  if (context?.calledRecipientIds) {
    return [...context.calledRecipientIds];
  }

  return [];
}

function resolveDeadlineDepartment(
  scheduleEvents: ScheduleEvent[],
  personnel: Personnel[],
  context: CommunicationAudienceContext | undefined
): string[] {
  if (context?.deadlineRecipientIds) {
    return [...context.deadlineRecipientIds];
  }

  const linkedSource = context?.linkedSource;
  if (linkedSource?.type !== 'deadline' || !linkedSource.id) {
    return [];
  }

  const event = scheduleEvents.find((scheduleEvent) => (
    scheduleEvent.id === linkedSource.id && scheduleEvent.type === 'deadline'
  ));
  return event ? deriveDeadlineRecipientIdsForEvent(event, personnel) : [];
}

function contextualLabel(key: ContextualAudienceKey): string {
  switch (key) {
    case 'meeting-attendees':
      return 'Meeting Attendees';
    case 'called-recipients':
      return 'Called Recipients';
    case 'deadline-department':
      return 'Deadline Department';
  }
}

function resolveContextualPersonnelIds(
  key: ContextualAudienceKey,
  input: ResolveCommunicationAudienceInput
): string[] {
  switch (key) {
    case 'meeting-attendees':
      return resolveMeetingAttendees(input.scheduleEvents, input.personnel, input.context);
    case 'called-recipients':
      return resolveCalledRecipients(input.context);
    case 'deadline-department':
      return resolveDeadlineDepartment(input.scheduleEvents, input.personnel, input.context);
  }
}

function collectInitiallySuppressedEmails(personnel: Personnel[]): Map<string, SuppressedEmail> {
  const suppressedEmails = new Map<string, SuppressedEmail>();
  for (const contact of personnel) {
    if (contact.deleted || !contact.emailOptOut || !hasUsableEmail(contact)) {
      continue;
    }
    const email = normalizeEmail(contact.email);
    addSuppressedEmail(suppressedEmails, {
      email,
      name: contact.name,
      personnelId: contact.id,
      reason: 'Email opt-out',
    });
  }
  return suppressedEmails;
}

export function resolveCommunicationAudience(
  input: ResolveCommunicationAudienceInput
): CommunicationRecipientSnapshot {
  const recipients: ResolvedCommunicationRecipient[] = [];
  const excludedRecipients: ExcludedCommunicationRecipient[] = [];
  const missingEmailRecipients: MissingEmailCommunicationRecipient[] = [];
  const personnelById = new Map(input.personnel.map((personnel) => [personnel.id, personnel]));
  const suppressedEmails = collectInitiallySuppressedEmails(input.personnel);
  const selectedAudiences: ProjectCommunicationAudience[] = [];
  const manualExclusions: ManualExclusion[] = [];

  for (const audienceId of input.selection.standingAudienceIds) {
    const audience = input.config.audiences.find((candidate) => candidate.id === audienceId);
    if (!audience || audience.disabled) {
      continue;
    }

    selectedAudiences.push(audience);
    const displayName = audienceDisplayName(audience);
    const standingSource: CommunicationRecipientSource = {
      type: 'standing-audience',
      audienceId: audience.id,
      audienceName: displayName,
      rule: audience.rule,
    };

    for (const personnel of input.personnel) {
      if (matchesRule(personnel, audience.rule)) {
        addRosterRecipient(
          recipients,
          excludedRecipients,
          missingEmailRecipients,
          suppressedEmails,
          personnel,
          displayName,
          standingSource
        );
      }
    }

    for (const personnelId of audience.manualIncludeContactIds) {
      const personnel = personnelById.get(personnelId);
      if (!personnel || personnel.deleted) {
        continue;
      }
      addRosterRecipient(
        recipients,
        excludedRecipients,
        missingEmailRecipients,
        suppressedEmails,
        personnel,
        `${displayName} manual include`,
        {
          type: 'manual-include',
          audienceId: audience.id,
          audienceName: displayName,
        }
      );
    }

    for (const personnelId of audience.manualExcludeContactIds) {
      manualExclusions.push({ audience, personnelId });
    }
  }

  for (const contextualAudienceKey of input.selection.contextualAudienceKeys) {
    const personnelIds = resolveContextualPersonnelIds(contextualAudienceKey, input);
    const label = contextualLabel(contextualAudienceKey);
    const source: CommunicationRecipientSource = {
      type: 'contextual',
      contextualAudienceKey,
      label,
    };

    for (const personnelId of personnelIds) {
      const personnel = personnelById.get(personnelId);
      if (!personnel || personnel.deleted) {
        continue;
      }
      addRosterRecipient(
        recipients,
        excludedRecipients,
        missingEmailRecipients,
        suppressedEmails,
        personnel,
        label,
        source
      );
    }
  }

  applyManualExclusions(
    recipients,
    excludedRecipients,
    missingEmailRecipients,
    suppressedEmails,
    manualExclusions,
    personnelById
  );

  for (const audience of selectedAudiences) {
    const displayName = audienceDisplayName(audience);
    for (const externalRecipient of audience.externalRecipients) {
      const normalizedEmail = normalizeEmail(externalRecipient.email);
      addExternalRecipient(
        recipients,
        excludedRecipients,
        suppressedEmails,
        externalRecipient,
        `${displayName} external`,
        {
          type: 'external',
          audienceId: audience.id,
          audienceName: displayName,
          email: normalizedEmail,
        }
      );
    }
  }

  return {
    createdAt: input.createdAt,
    standingAudienceIds: [...input.selection.standingAudienceIds],
    contextualAudienceKeys: [...input.selection.contextualAudienceKeys],
    ...(input.context?.linkedSource ? { linkedSource: input.context.linkedSource } : {}),
    recipients,
    excludedRecipients,
    missingEmailRecipients,
  };
}
